// ============================================================
// PERRY — OPTIONS
// Presentation only. These are device preferences, not player
// progress, so they live in localStorage and NEVER in Firebase —
// no new profile fields, ever (Firebase Law #5).
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, KeyGlyph, ensureKeyframes } from "./PerryUI";
import { GUARD, RULES, SLOT_KEYS, hueOf } from "./perryData";

const KF = `@keyframes optIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }`;

const KEY = "perry:options";
// `hints: "auto"` is the shipping default and it is not a boolean
// on purpose. AUTO means: on until Hollowmere has been reached for
// the first time, off forever after. A player who wants them back
// can pin them ON; a player who never wants them can pin them OFF.
// (caP'n's call, 2026-07-27 — training wheels that take themselves
// off are the only kind anybody actually uses.)
const DEFAULTS = { shake: true, letterbox: true, grain: true, subtitles: true, hints: "auto" };

export const loadOptions = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch (_) {
    return { ...DEFAULTS };
  }
};

export const saveOptions = (o) => {
  try { window.localStorage.setItem(KEY, JSON.stringify(o)); } catch (_) { /* private mode */ }
};

function Toggle({ label, hint, on, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14,
        padding: "12px 15px", marginBottom: 8, borderRadius: 10, cursor: "pointer",
        background: "linear-gradient(175deg, #1c1009, #0d0705)",
        border: `1px solid ${on ? GM.inkGoldDim : GM.pageBorder}`,
      }}
    >
      <div>
        <div style={{ fontSize: 13, letterSpacing: 2, color: GM.vellum }}>{label}</div>
        <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 3 }}>{hint}</div>
      </div>
      <div style={{
        width: 46, height: 24, borderRadius: 12, flexShrink: 0,
        background: on ? "linear-gradient(90deg, #7a4218, #c8763a)" : "#1a120c",
        border: `1px solid ${on ? GM.inkGold : GM.pageBorder}`,
        boxShadow: on ? `0 0 14px ${GM.inkGoldDim}` : "inset 0 2px 5px rgba(0,0,0,0.7)",
        position: "relative", transition: "all 0.18s ease",
      }}>
        <div style={{
          position: "absolute", top: 2, left: on ? 24 : 2,
          width: 18, height: 18, borderRadius: "50%",
          background: on ? "#ffe6c2" : "#4a3a30",
          transition: "left 0.18s ease",
        }} />
      </div>
    </div>
  );
}

export default function OptionsMenu({ onBack }) {
  ensureKeyframes("perry-options-keyframes", KF);
  const [opts, setOpts] = useState(loadOptions);

  useEffect(() => { saveOptions(opts); }, [opts]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !e.repeat) onBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const flip = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      overflow: "auto", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={12} />

      <div style={{
        position: "relative", minHeight: "100%", display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "40px 20px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: 30, letterSpacing: 10, color: GM.vellum,
            textShadow: `0 0 30px ${GM.inkGold}`,
          }}>OPTIONS</div>
          <MetalRule width={190} style={{ margin: "9px auto 0" }} />
        </div>

        <div style={{ width: "min(460px, 94vw)", animation: "optIn 0.45s ease-out" }}>
          <Toggle label="SCREEN SHAKE" hint="The road hits back" on={opts.shake} onClick={() => flip("shake")} />
          <Toggle label="LETTERBOX" hint="Cinema bars during the run" on={opts.letterbox} onClick={() => flip("letterbox")} />
          <Toggle label="FILM GRAIN" hint="Ash on the lens" on={opts.grain} onClick={() => flip("grain")} />
          <Toggle label="SUBTITLES" hint="The narration, written out" on={opts.subtitles} onClick={() => flip("subtitles")} />
          <Toggle
            label="THE COALS"
            hint={
              opts.hints === "auto"
                ? "AUTO — lettered until Hollowmere, coloured after, and whatever the foe leaves you past that"
                : opts.hints === true ? "ALWAYS LETTERED — no foe may ever move them or put them out"
                : "OFF — no coals at any tier, and no charge is ever spent. Read the creature."
            }
            on={opts.hints !== false}
            onClick={() => setOpts((o2) => {
              // auto → on → off → auto
              const nx = o2.hints === "auto" ? true : o2.hints === true ? false : "auto";
              const next = { ...o2, hints: nx };
              saveOptions(next);
              return next;
            })}
          />

          <div style={{
            marginTop: 20, padding: "16px 18px", borderRadius: 12,
            background: "linear-gradient(175deg, #1c1009, #0d0705)",
            border: `1px solid ${GM.pageBorder}`,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 4, color: GM.inkGoldBright, marginBottom: 12 }}>
              CONTROLS
            </div>
            {/* The grid, drawn as the grid — left column is your
                left hand, each row is the same finger on both. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {GUARD.ROWS.map((row, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{
                    fontSize: 9, letterSpacing: 3, color: GM.vellumFaint,
                    width: 34, textAlign: "right", marginRight: 4,
                  }}>{GUARD.ROW_NAMES[i]}</div>
                  {row.map((k) => <KeyGlyph key={k} k={k} size={30} active tint={hueOf(k)} />)}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.55, marginBottom: 12 }}>
              The guard grid. Your left hand holds the left column, your right hand the right, and each row is the same finger on both — index on top, ring on the bottom. Every key owns a colour, and the row is the family: HIGH warm, MID cold, LOW bright. Learn the colours — the letters do not follow you out of the tutorial, and something on the road will eventually move the coals or put them out. Nothing on screen tells you which one is coming. The creature does.
            </div>
            {/* ── THE GRIMOIRE'S KEYS, 2026-07-28 ──
                This used to list the three Imperas against the
                three keys they were nailed to. Powers are not
                nailed to keys any more — the KEYS belong to the
                SLOTS, and what is in a slot is your decision, made
                in KIT UP. So this can no longer name anything; it
                can only say which fingers the rack answers to.
                Which is, on reflection, the more useful sentence. */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8, flexWrap: "wrap" }}>
              {SLOT_KEYS.map((k) => <KeyGlyph key={k} k={k} size={26} />)}
              <span style={{ fontSize: 11, color: GM.vellumDim, marginLeft: 4 }}>
                your Grimoire — the {RULES.SLOTS} powers you kitted up with
              </span>
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.55, marginBottom: 12 }}>
              Which power sits on which number is yours to set, in KIT UP → ◈ GRIMOIRE. You will know
              more of them than you can carry, and that is the point — both paws live on the guard
              during a fight, so every number you reach for is a hand that is not defending you.
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8 }}>
              ESC — back, anywhere.
            </div>
          </div>

          <div style={{
            marginTop: 16, padding: "16px 18px", borderRadius: 12,
            background: "linear-gradient(175deg, #150c07, #0a0605)",
            border: `1px solid ${GM.pageBorder}`,
            fontSize: 11, lineHeight: 1.7, color: GM.vellumFaint,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 4, color: GM.inkGoldBright, marginBottom: 10 }}>
              CREDITS
            </div>
            PERRY — an Illegal Empire title.<br />
            Built by caP'n, with V'ael the Gamemaster.<br />
            Runs on the shared Empire account: your Divine Crests,
            records and achievements travel with you to every other title.<br /><br />
            <span style={{ color: GM.vellumDim }}>Bebs should not be slept on.</span>
          </div>

          <button onClick={onBack} style={{
            width: "100%", marginTop: 18, padding: "12px 0", borderRadius: 10, cursor: "pointer",
            background: "linear-gradient(175deg, #1c1009, #0d0705)",
            border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
            fontFamily: GM.font, fontSize: 12, letterSpacing: 4,
          }}>← BACK</button>
        </div>
      </div>
    </div>
  );
}
